/**
 * Overview Data Context
 * Manages all data fetching and state for the Overview/Dashboard page
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useLanguage } from './AppContext';
import { getFriendlyApiErrorMessage } from '../utils/errorUtils';
import { dashboardService, attributionService } from '../services';
import { getCachedOrFetch, preloadCache } from '../services/cache';
import {
  DashboardMetrics,
  PerformanceHistory,
  PerformanceTabResponse,
  TradingSignalsResponse,
  PortfolioAttribution,
  Period,
  SignalPeriod,
  CrossFilterResponse,
  MarketSummary,
  ChartDistributionItem,
} from '../types';

// ============================================
// Types
// ============================================

interface DistributionsState {
  sector: ChartDistributionItem[];
  rating: ChartDistributionItem[];
  profile: ChartDistributionItem[];
  size: ChartDistributionItem[];
  country: ChartDistributionItem[];
  maturity: ChartDistributionItem[];
}

interface OverviewDataState {
  // Dashboard Metrics
  metrics: DashboardMetrics | null;
  metricsLoading: boolean;
  metricsError: string | null;

  // Performance History
  performanceHistory: PerformanceHistory | null;
  performanceLoading: boolean;
  performanceError: string | null;

  // Performance Tab (rankings, implied vol/spread, delta-neutral 1D) – backend only
  performanceTab: PerformanceTabResponse | null;
  performanceTabLoading: boolean;
  performanceTabError: string | null;

  // Trading Signals
  tradingSignals: TradingSignalsResponse | null;
  signalsLoading: boolean;
  signalsError: string | null;

  // Portfolio Attribution
  attribution: PortfolioAttribution | null;
  attributionLoading: boolean;
  attributionError: string | null;

  // Distributions for charts
  distributions: DistributionsState;
  distributionsLoading: boolean;
  distributionsError: string | null;

  // Selected periods
  performancePeriod: Period;
  signalsPeriod: SignalPeriod;
  attributionPeriod: Period;
}

interface OverviewDataContextType extends OverviewDataState {
  // Period setters
  setPerformancePeriod: (period: Period) => void;
  setSignalsPeriod: (period: SignalPeriod) => void;
  setAttributionPeriod: (period: Period) => void;

  // Refetch functions
  refetchMetrics: () => Promise<void>;
  refetchPerformance: () => Promise<void>;
  refetchPerformanceTab: (period: Period, impliedVolPeriod: Period, impliedSpreadPeriod: Period) => Promise<void>;
  refetchSignals: () => Promise<void>;
  refetchAttribution: () => Promise<void>;
  refetchDistributions: () => Promise<void>;
  refetchAll: () => Promise<void>;

  /** Load data for a tab (only fetches if not already loaded). Call when user switches tab. */
  loadTab: (tab: 'overview' | 'portfolio' | 'signals') => void;

  // Global loading state
  isLoading: boolean;
}

// ============================================
// Initial State
// ============================================

const emptyDistributions: DistributionsState = {
  sector: [],
  rating: [],
  profile: [],
  size: [],
  country: [],
  maturity: [],
};

const initialState: OverviewDataState = {
  metrics: null,
  metricsLoading: false,
  metricsError: null,

  performanceHistory: null,
  performanceLoading: false,
  performanceError: null,

  performanceTab: null,
  performanceTabLoading: false,
  performanceTabError: null,

  tradingSignals: null,
  signalsLoading: false,
  signalsError: null,

  attribution: null,
  attributionLoading: false,
  attributionError: null,

  distributions: emptyDistributions,
  distributionsLoading: false,
  distributionsError: null,

  performancePeriod: 'YTD',
  signalsPeriod: '10D',
  attributionPeriod: 'MTD',
};

// ============================================
// Helper Functions
// ============================================

/**
 * Convert CrossFilterResponse to ChartDistributionItem array
 */
const mapCrossFilterToDistribution = (
  response: CrossFilterResponse
): ChartDistributionItem[] => {
  if (!response.primary || response.primary.length === 0) {
    return [];
  }

  const totalNotional = response.totalNotional || 1;
  
  return response.primary.map(item => ({
    name: item.name,
    value: item.cbMarketCap,
    count: item.count,
    percent: (item.cbMarketCap / totalNotional) * 100,
    marketCap: item.cbMarketCap,
  }));
};

/**
 * Convert country distribution from summary to ChartDistributionItem array
 */
const mapCountryDistribution = (
  countryDist: Record<string, number>,
  totalBonds: number
): ChartDistributionItem[] => {
  return Object.entries(countryDist).map(([name, count]) => ({
    name,
    value: count,
    count,
    percent: (count / totalBonds) * 100,
  }));
};

// ============================================
// Context
// ============================================

const OverviewDataContext = createContext<OverviewDataContextType | undefined>(undefined);

// Module-level set so loaded tabs persist across Strict Mode remounts (avoids duplicate requests)
const loadedTabsSet = new Set<'overview' | 'portfolio' | 'signals'>();

// ============================================
// Provider
// ============================================

export const OverviewDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useLanguage();
  const [state, setState] = useState<OverviewDataState>(initialState);

  // Clear loaded tabs when leaving dashboard so data refetches on next visit
  React.useEffect(() => () => { loadedTabsSet.clear(); }, []);

  // Preload cache for common endpoints on mount (background, non-blocking)
  React.useEffect(() => {
    preloadCache([
      { key: 'overview-metrics', fetcher: () => dashboardService.getDashboardMetrics() },
      { key: 'overview-distributions', fetcher: () => dashboardService.getAllDistributions() },
      { key: `overview-performance-YTD`, fetcher: () => dashboardService.getPerformanceHistory('YTD') },
    ]);
  }, []);

  // Fetch dashboard metrics (cached 5 min)
  const fetchMetrics = useCallback(async () => {
    setState(prev => ({ ...prev, metricsLoading: true, metricsError: null }));
    try {
      const metrics = await getCachedOrFetch(
        'overview-metrics',
        () => dashboardService.getDashboardMetrics(),
        (data) => setState(prev => ({ ...prev, metrics: data, metricsLoading: false }))
      );
      setState(prev => ({ ...prev, metrics, metricsLoading: false }));
    } catch (err: any) {
      const error = getFriendlyApiErrorMessage(err, t);
      setState(prev => ({ ...prev, metricsLoading: false, metricsError: error }));
    }
  }, [t]);

  // Fetch performance history (cached 5 min per period)
  const fetchPerformance = useCallback(async (period: Period) => {
    setState(prev => ({ ...prev, performanceLoading: true, performanceError: null }));
    try {
      const performanceHistory = await getCachedOrFetch(
        `overview-performance-${period}`,
        () => dashboardService.getPerformanceHistory(period),
        (data) => setState(prev => ({ ...prev, performanceHistory: data, performanceLoading: false }))
      );
      setState(prev => ({ ...prev, performanceHistory, performanceLoading: false }));
    } catch (err: any) {
      const error = getFriendlyApiErrorMessage(err, t);
      setState(prev => ({ ...prev, performanceLoading: false, performanceError: error }));
    }
  }, [t]);

  // Fetch performance tab (cached 5 min per params)
  const fetchPerformanceTab = useCallback(async (period: Period, impliedVolPeriod: Period, impliedSpreadPeriod: Period) => {
    setState(prev => ({ ...prev, performanceTabLoading: true, performanceTabError: null }));
    const key = `overview-performanceTab-${period}-${impliedVolPeriod}-${impliedSpreadPeriod}`;
    try {
      const performanceTab = await getCachedOrFetch(
        key,
        () => dashboardService.getPerformanceTab(period, impliedVolPeriod, impliedSpreadPeriod),
        (data) => setState(prev => ({ ...prev, performanceTab: data, performanceTabLoading: false }))
      );
      setState(prev => ({ ...prev, performanceTab, performanceTabLoading: false }));
    } catch (err: any) {
      const error = getFriendlyApiErrorMessage(err, t);
      setState(prev => ({ ...prev, performanceTab: null, performanceTabLoading: false, performanceTabError: error }));
    }
  }, [t]);

  // Fetch trading signals (cached 5 min per period)
  const fetchSignals = useCallback(async (period: SignalPeriod) => {
    setState(prev => ({ ...prev, signalsLoading: true, signalsError: null }));
    try {
      const tradingSignals = await getCachedOrFetch(
        `overview-signals-${period}`,
        () => dashboardService.getTradingSignals(period),
        (data) => setState(prev => ({ ...prev, tradingSignals: data, signalsLoading: false }))
      );
      setState(prev => ({ ...prev, tradingSignals, signalsLoading: false }));
    } catch (err: any) {
      const error = getFriendlyApiErrorMessage(err, t);
      setState(prev => ({ ...prev, signalsLoading: false, signalsError: error }));
    }
  }, [t]);

  // Fetch portfolio attribution (cached 5 min per period)
  const fetchAttribution = useCallback(async (period: Period) => {
    setState(prev => ({ ...prev, attributionLoading: true, attributionError: null }));
    try {
      const attribution = await getCachedOrFetch(
        `overview-attribution-${period}`,
        () => attributionService.getPortfolioAttribution(period),
        (data) => setState(prev => ({ ...prev, attribution: data, attributionLoading: false }))
      );
      setState(prev => ({ ...prev, attribution, attributionLoading: false }));
    } catch (err: any) {
      const error = getFriendlyApiErrorMessage(err, t);
      setState(prev => ({ ...prev, attributionLoading: false, attributionError: error }));
    }
  }, [t]);

  // Fetch all distributions (cached 5 min)
  const fetchDistributions = useCallback(async () => {
    setState(prev => ({ ...prev, distributionsLoading: true, distributionsError: null }));
    try {
      const raw = await getCachedOrFetch(
        'overview-distributions',
        () => dashboardService.getAllDistributions(),
        (data) => {
          const distributions: DistributionsState = {
            sector: mapCrossFilterToDistribution(data.sector),
            rating: mapCrossFilterToDistribution(data.rating),
            profile: mapCrossFilterToDistribution(data.profile),
            size: mapCrossFilterToDistribution(data.size),
            country: mapCountryDistribution(data.summary.countryDistribution, data.summary.totalBonds),
            maturity: [],
          };
          setState(prev => ({ ...prev, distributions, distributionsLoading: false }));
        }
      );
      const distributions: DistributionsState = {
        sector: mapCrossFilterToDistribution(raw.sector),
        rating: mapCrossFilterToDistribution(raw.rating),
        profile: mapCrossFilterToDistribution(raw.profile),
        size: mapCrossFilterToDistribution(raw.size),
        country: mapCountryDistribution(raw.summary.countryDistribution, raw.summary.totalBonds),
        maturity: [],
      };
      setState(prev => ({ ...prev, distributions, distributionsLoading: false }));
    } catch (err: any) {
      const error = getFriendlyApiErrorMessage(err, t);
      setState(prev => ({ ...prev, distributionsLoading: false, distributionsError: error }));
    }
  }, [t]);

  const loadTab = useCallback(
    (tab: 'overview' | 'portfolio' | 'signals') => {
      if (loadedTabsSet.has(tab)) return;
      loadedTabsSet.add(tab);

      if (tab === 'overview') {
        fetchMetrics();
        fetchDistributions();
        fetchPerformance(state.performancePeriod);
      } else if (tab === 'portfolio') {
        fetchPerformance(state.performancePeriod);
        fetchPerformanceTab(state.performancePeriod, '1D', '1D');
        fetchAttribution(state.attributionPeriod);
      } else if (tab === 'signals') {
        fetchSignals(state.signalsPeriod);
      }
    },
    [
      state.performancePeriod,
      fetchMetrics,
      fetchDistributions,
      fetchPerformance,
      fetchPerformanceTab,
      fetchSignals,
      fetchAttribution,
    ]
  );

  // Period change handlers
  const setPerformancePeriod = useCallback((period: Period) => {
    setState(prev => ({ ...prev, performancePeriod: period }));
    fetchPerformance(period);
  }, [fetchPerformance]);

  const setSignalsPeriod = useCallback((period: SignalPeriod) => {
    setState(prev => ({ ...prev, signalsPeriod: period }));
    fetchSignals(period);
  }, [fetchSignals]);

  const setAttributionPeriod = useCallback((period: Period) => {
    setState(prev => ({ ...prev, attributionPeriod: period }));
    fetchAttribution(period);
  }, [fetchAttribution]);

  // Refetch functions
  const refetchMetrics = useCallback(() => fetchMetrics(), [fetchMetrics]);
  const refetchPerformance = useCallback(() => fetchPerformance(state.performancePeriod), [fetchPerformance, state.performancePeriod]);
  const refetchPerformanceTab = useCallback(
    (period: Period, impliedVolPeriod: Period, impliedSpreadPeriod: Period) =>
      fetchPerformanceTab(period, impliedVolPeriod, impliedSpreadPeriod),
    [fetchPerformanceTab]
  );
  const refetchSignals = useCallback(() => fetchSignals(state.signalsPeriod), [fetchSignals, state.signalsPeriod]);
  const refetchAttribution = useCallback(() => fetchAttribution(state.attributionPeriod), [fetchAttribution, state.attributionPeriod]);
  const refetchDistributions = useCallback(() => fetchDistributions(), [fetchDistributions]);

  const refetchAll = useCallback(async () => {
    await Promise.all([
      fetchMetrics(),
      fetchPerformance(state.performancePeriod),
      fetchPerformanceTab(state.performancePeriod, '1D', '1D'),
      fetchSignals(state.signalsPeriod),
      fetchAttribution(state.attributionPeriod),
      fetchDistributions(),
    ]);
  }, [fetchMetrics, fetchPerformance, fetchPerformanceTab, fetchSignals, fetchAttribution, fetchDistributions, state.performancePeriod, state.signalsPeriod, state.attributionPeriod]);

  // Global loading state
  const isLoading = state.metricsLoading || state.performanceLoading || state.performanceTabLoading || state.signalsLoading || state.attributionLoading || state.distributionsLoading;

  return (
    <OverviewDataContext.Provider
      value={{
        ...state,
        setPerformancePeriod,
        setSignalsPeriod,
        setAttributionPeriod,
        refetchMetrics,
        refetchPerformance,
        refetchPerformanceTab,
        refetchSignals,
        refetchAttribution,
        refetchDistributions,
        refetchAll,
        loadTab,
        isLoading,
      }}
    >
      {children}
    </OverviewDataContext.Provider>
  );
};

// ============================================
// Hook
// ============================================

export const useOverviewData = (): OverviewDataContextType => {
  const context = useContext(OverviewDataContext);
  if (context === undefined) {
    throw new Error('useOverviewData must be used within an OverviewDataProvider');
  }
  return context;
};

export default OverviewDataContext;
